/**
 * Loose person-name matcher for board filters.
 *
 * Legacy RestorePM data stores the same employee in several formats
 * across project records — e.g. "Pinto, Kelita", "K.Pinto", "Kelita Pinto".
 * The Edit Filters dropdown only offers one canonical version, so a naive
 * substring match misses the other variants.
 *
 * `nameKey` reduces any of those formats to { last, initial } and
 * `namesMatch` returns true when:
 *   - both sides share the same last-name token, AND
 *   - either side is missing a first initial, OR they agree on it.
 *
 * Examples (all match):
 *   "Pinto, Kelita"  vs  "K.Pinto"        -> { last: "pinto", initial: "k" }
 *   "Kelita Pinto"   vs  "Pinto, Kelita"  -> "
 *   "Pinto"          vs  "K.Pinto"        -> last only match
 *
 * Falls back to substring matching if either side can't be parsed.
 */
function nameKey(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  // Format 1: "Last, First" — comma separates last name from first.
  if (raw.includes(',')) {
    const [lastPart, firstPart = ''] = raw.split(',').map((s) => s.trim());
    if (!lastPart) return null;
    return {
      last: lastPart.toLowerCase(),
      initial: firstPart ? firstPart[0].toLowerCase() : '',
    };
  }

  // Format 2: "F.Last" or "First.Last" — dot separates initial(s) from last.
  if (raw.includes('.')) {
    const parts = raw.split('.').map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const lastToken = parts[parts.length - 1];
      const firstToken = parts[0];
      return {
        last: lastToken.toLowerCase(),
        initial: firstToken ? firstToken[0].toLowerCase() : '',
      };
    }
    if (parts.length === 1) {
      return { last: parts[0].toLowerCase(), initial: '' };
    }
    return null;
  }

  // Format 3: "First Last" / just "Last" — last token wins.
  const tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return null;
  if (tokens.length === 1) return { last: tokens[0].toLowerCase(), initial: '' };
  return {
    last: tokens[tokens.length - 1].toLowerCase(),
    initial: tokens[0][0].toLowerCase(),
  };
}

export function namesMatch(filterValue, projectValue) {
  if (!filterValue) return true;
  const a = nameKey(filterValue);
  const b = nameKey(projectValue);
  if (!a || !b) {
    // Couldn't parse — fall back to substring so we don't regress simple cases.
    return String(projectValue || '').toLowerCase().includes(String(filterValue).toLowerCase());
  }
  if (a.last !== b.last) return false;
  if (a.initial && b.initial && a.initial !== b.initial) return false;
  return true;
}

export default namesMatch;
